import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useWindowSize } from '../components/useWindowSize';
import axios from 'axios';

const FWD_PAIRS = ['USDINR', 'EURINR', 'GBPINR', 'JPYINR', 'EURUSD'];

const SPOT_SOURCE = {
  'USDINR': 'USDINRCOMP',
  'EURINR': 'EURINRCOMP',
  'GBPINR': 'GBPINRCOMP',
  'JPYINR': 'JPYINR',
  'EURUSD': 'EURUSD',
};

const parseDate = (str) => {
  try {
    const months = { Jan:0,Feb:1,Mar:2,Apr:3,May:4,Jun:5,Jul:6,Aug:7,Sep:8,Oct:9,Nov:10,Dec:11 };
    const parts = str.split('-');
    return new Date(parseInt(parts[2]), months[parts[1]], parseInt(parts[0]));
  } catch { return new Date('invalid'); }
};

export default function ForwardRates() {
  const { API }                           = useAuth();
  const isMobile                          = useWindowSize();
  const [forwardData, setForwardData]     = useState({});
  const [spotRates, setSpotRates]         = useState({});
  const [activePair, setActivePair]       = useState('USDINR');
  const [lastFwdUpdate, setLastFwdUpdate] = useState(null);
  const [showCalc, setShowCalc]           = useState(false);
  const [selDate, setSelDate]             = useState('');
  const [calcPair, setCalcPair]           = useState('USDINR');

  useEffect(() => {
    fetchForwardData();
    const iv = setInterval(fetchForwardData, 30000);
    return () => clearInterval(iv);
  }, []);

  useEffect(() => {
    fetchSpot();
    const iv = setInterval(fetchSpot, 333);
    return () => clearInterval(iv);
  }, []);

  const fetchForwardData = async () => {
    try {
      const res = await axios.get(API + '/rates/forward');
      const grouped = {};
      res.data.forEach(r => {
        if (!grouped[r.currencyPair]) {
          grouped[r.currencyPair] = {
            spotSettlementDate: r.spotSettlementDate,
            tenors: [],
          };
        }
        grouped[r.currencyPair].tenors.push(r);
      });
      setForwardData(grouped);
      setLastFwdUpdate(new Date());
    } catch (e) {}
  };

  const fetchSpot = async () => {
    try {
      const res = await axios.get(API + '/rates/live');
      const map = {};
      res.data.forEach(r => { map[r.currencyPair] = r; });
      setSpotRates(map);
    } catch (e) {}
  };

  const calcDays = (pillarStr, spotSettleStr) => {
    try {
      const today  = new Date(); today.setHours(0,0,0,0);
      const spot   = parseDate(spotSettleStr);
      const pillar = parseDate(pillarStr);
      const offset = Math.round((spot - today) / 86400000);
      const days   = Math.round((pillar - today) / 86400000) - offset;
      return days > 0 ? days : 0;
    } catch { return 0; }
  };

  const calcFwd = (spotBid, spotAsk, bidPct, askPct, days) => {
    if (!spotBid || !spotAsk || days <= 0) return null;
    return {
      fwdBid: (spotBid * (1 + (bidPct / 100) * days / 365)).toFixed(4),
      fwdAsk: (spotAsk * (1 + (askPct / 100) * days / 365)).toFixed(4),
    };
  };

  const getLiveSpot = (pair) => {
    const src = SPOT_SOURCE[pair] || pair;
    return spotRates[src] || null;
  };

  const getBrokenResult = () => {
    if (!selDate) return null;
    const pairData = forwardData[calcPair];
    if (!pairData) return null;
    const spot       = getLiveSpot(calcPair);
    const spotBid    = spot?.bid || 0;
    const spotAsk    = spot?.ask || 0;
    const selected   = new Date(selDate);
    const today      = new Date(); today.setHours(0,0,0,0);
    const spotSettle = parseDate(pairData.spotSettlementDate);
    const offset     = Math.round((spotSettle - today) / 86400000);
    const day        = selected.getDate();
    const tenors     = pairData.tenors;
    let lower = null, upper = null;
    for (let t of tenors) {
      const p = parseDate(t.pillarDate);
      if (p <= selected) lower = t;
      else if (!upper)   upper = t;
    }
    let bidPct = 0, askPct = 0;
    if (day >= 1 && day <= 9) {
      bidPct = lower?.bidPct || 0;
      askPct = lower?.askPct || 0;
    } else if (day >= 10 && day <= 20) {
      bidPct = ((lower?.bidPct||0) + (upper?.bidPct||0)) / 2;
      askPct = ((lower?.askPct||0) + (upper?.askPct||0)) / 2;
    } else {
      bidPct = upper?.bidPct || 0;
      askPct = upper?.askPct || 0;
    }
    const days   = Math.round((selected - today) / 86400000) - offset;
    const result = calcFwd(spotBid, spotAsk, bidPct, askPct, days);
    return {
      pair: calcPair, date: selDate, days,
      spotBid: spotBid.toFixed(4), spotAsk: spotAsk.toFixed(4),
      bidPct: bidPct.toFixed(4), askPct: askPct.toFixed(4),
      fwdBid: result?.fwdBid || '--',
      fwdAsk: result?.fwdAsk || '--',
    };
  };

  const pairData    = forwardData[activePair];
  const spotForPair = getLiveSpot(activePair);
  const availPairs  = FWD_PAIRS.filter(p => forwardData[p]);
  const calcResult  = selDate ? getBrokenResult() : null;
  const fmt = (v, dp=4) => { try { return parseFloat(v).toFixed(dp); } catch { return '--'; } };

  return (
    <div style={{ minHeight: '100vh', background: '#F8FAFC', paddingBottom: '80px' }}>

      {/* HEADER */}
      <div style={{
        background: '#1B3A6B', padding: '12px 16px',
        display: 'flex', alignItems: 'center',
        justifyContent: 'space-between',
        position: 'sticky', top: 0, zIndex: 50,
      }}>
        <div>
          <div style={{ fontFamily: 'Arial', fontSize: '14px', fontWeight: 700, color: '#D4A843', letterSpacing: '0.08em' }}>
            FORWARD RATES
          </div>
          <div style={{ fontFamily: 'Arial', fontSize: '9px', color: '#93C5FD', marginTop: '2px' }}>
            {lastFwdUpdate ? 'Fwd: ' + lastFwdUpdate.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false }) : 'Loading...'}
            {'  •  Spot: live 3x/sec'}
          </div>
        </div>
        <button
          onClick={() => setShowCalc(!showCalc)}
          style={{
            background: showCalc ? '#8B6914' : '#D4A843',
            border: 'none', borderRadius: '20px',
            padding: '8px 16px', color: '#1B3A6B',
            fontFamily: 'Arial', fontSize: '12px',
            fontWeight: 700, cursor: 'pointer',
          }}>
          {showCalc ? 'CLOSE' : 'CUSTOM FWD RATE'}
        </button>
      </div>

      {/* MAIN LAYOUT */}
      <div style={{
        display: isMobile ? 'block' : 'flex',
        gap: '20px',
        padding: isMobile ? '0' : '20px 24px',
        maxWidth: '1200px',
        margin: '0 auto',
        alignItems: 'flex-start',
      }}>

        {/* LEFT — FORWARD TABLE */}
        <div style={{ flex: 1 }}>

          {/* Pair selector */}
          <div style={{
            background: '#fff', padding: '12px 16px',
            borderBottom: '1px solid #E5E7EB',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
              <span style={{ fontFamily: 'Arial', fontSize: '10px', fontWeight: 600, color: '#6B7280', letterSpacing: '0.08em', whiteSpace: 'nowrap' }}>
                CURRENCY
              </span>
              <select
                value={activePair}
                onChange={e => setActivePair(e.target.value)}
                style={{
                  flex: 1, background: '#F8FAFC',
                  border: '1px solid #E5E7EB',
                  borderRadius: '8px', padding: '8px 12px',
                  fontSize: '14px', fontWeight: 600,
                  color: '#111827', outline: 'none',
                }}>
                {(availPairs.length > 0 ? availPairs : FWD_PAIRS).map(p => (
                  <option key={p} value={p}>{p}</option>
                ))}
              </select>
            </div>

            {/* Spot info */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
              <span style={{ fontFamily: 'Arial', fontSize: '9px', fontWeight: 700, color: '#6B7280', letterSpacing: '0.1em' }}>SPOT</span>
              <span style={{ fontFamily: 'monospace', fontSize: '12px', color: '#16A34A', fontWeight: 600 }}>
                Bid {spotForPair ? fmt(spotForPair.bid) : '--'}
              </span>
              <span style={{ fontFamily: 'monospace', fontSize: '12px', color: '#DC2626', fontWeight: 600 }}>
                Ask {spotForPair ? fmt(spotForPair.ask) : '--'}
              </span>
              {SPOT_SOURCE[activePair] !== activePair && (
                <span style={{ background: '#FEF3C7', color: '#D97706', fontFamily: 'Arial', fontSize: '9px', fontWeight: 600, padding: '2px 6px', borderRadius: '4px', border: '1px solid #FDE68A' }}>
                  {SPOT_SOURCE[activePair]}
                </span>
              )}
              {pairData?.spotSettlementDate && (
                <span style={{ fontFamily: 'Arial', fontSize: '10px', color: '#D97706', marginLeft: 'auto' }}>
                  Settle: {pairData.spotSettlementDate}
                </span>
              )}
            </div>
          </div>

          {/* Table */}
          {pairData ? (
            <div style={{ background: '#fff', margin: isMobile ? '12px' : '12px 0', borderRadius: '12px', overflow: 'hidden', border: '1px solid #E5E7EB' }}>

              {/* Table Header */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 0.8fr 0.8fr 1fr 1fr 1.2fr', background: '#1B3A6B', padding: '10px 16px' }}>
                {['TENOR', 'BID %', 'FWD BID', 'ASK %', 'FWD ASK', 'PILLAR'].map(h => (
                  <div key={h} style={{ fontFamily: 'Arial', fontSize: '10px', fontWeight: 600, color: '#93C5FD', letterSpacing: '0.06em', textAlign: 'center' }}>{h}</div>
                ))}
              </div>

              {/* Table Rows */}
              {pairData.tenors.map((tenor, i) => {
                const days   = calcDays(tenor.pillarDate, pairData.spotSettlementDate);
                const result = calcFwd(spotForPair?.bid, spotForPair?.ask, tenor.bidPct, tenor.askPct, days);
                return (
                  <div key={i} style={{ display: 'grid', gridTemplateColumns: '1fr 0.7fr 1fr 0.7fr 1fr 1fr', padding: '8px 12px', background: i % 2 === 0 ? '#fff' : '#F8FAFC', borderBottom: '1px solid #F3F4F6' }}>
                    <div style={{ fontFamily: 'Arial', fontSize: '13px', fontWeight: 600, color: '#111827', textAlign: 'center' }}>
                      {tenor.tenor}
                    </div>
                    <div style={{ fontFamily: 'monospace', fontSize: '10px', color: '#D97706', fontWeight: 500, textAlign: 'center' }}>
                      {tenor.bidPct?.toFixed(2) || '--'}
                    </div>
                    <div style={{ fontFamily: 'monospace', fontSize: '15px', color: '#16A34A', fontWeight: 800, textAlign: 'center' }}>
                      {result?.fwdBid || '--'}
                    </div>
                    <div style={{ fontFamily: 'monospace', fontSize: '10px', color: '#D97706', fontWeight: 500, textAlign: 'center' }}>
                      {tenor.askPct?.toFixed(2) || '--'}
                    </div>
                    <div style={{ fontFamily: 'monospace', fontSize: '15px', color: '#DC2626', fontWeight: 800, textAlign: 'center' }}>
                      {result?.fwdAsk || '--'}
                    </div>
                    <div style={{ fontFamily: 'Arial', fontSize: '11px', color: '#6B7280', textAlign: 'center', lineHeight: 1.3 }}>
                      {(() => { try { const p = tenor.pillarDate.split('-'); return p[0] + '\n' + p[1] + ' ' + p[2].slice(2); } catch { return tenor.pillarDate; } })()}
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div style={{ textAlign: 'center', padding: '40px', color: '#9CA3AF', fontFamily: 'Arial' }}>
              No data for {activePair}. Please wait for next update.
            </div>
          )}

          <div style={{ textAlign: 'center', fontSize: '10px', color: '#9CA3AF', fontFamily: 'Arial', padding: '8px' }}>
            Forward rates are indicative only. Spot source: CCIL rate.
          </div>
        </div>

        {/* RIGHT — DESKTOP CALC PANEL */}
        {!isMobile && showCalc && (
          <div style={{
            width: '340px', flexShrink: 0,
            background: '#fff', borderRadius: '12px',
            border: '1px solid #E5E7EB',
            position: 'sticky', top: '80px',
            overflow: 'visible',
          }}>
            <CalcPanel
              calcPair={calcPair}
              setCalcPair={setCalcPair}
              selDate={selDate}
              setSelDate={setSelDate}
              calcResult={calcResult}
              onClose={() => { setShowCalc(false); setSelDate(''); }}
            />
          </div>
        )}
      </div>

      {/* MOBILE — BOTTOM SHEET */}
      {isMobile && showCalc && (
        <div
          onClick={e => { if (e.target === e.currentTarget) { setShowCalc(false); setSelDate(''); } }}
          style={{
            position: 'fixed', inset: 0,
            background: 'rgba(0,0,0,0.5)',
            zIndex: 1000,
            display: 'flex',
            alignItems: 'flex-end',
          }}>
          <div style={{
            background: '#fff',
            borderRadius: '20px 20px 0 0',
            width: '100%',
            maxHeight: '90vh',
            overflowY: 'auto',
            position: 'relative',
            zIndex: 1001,
          }}>
            <CalcPanel
              calcPair={calcPair}
              setCalcPair={setCalcPair}
              selDate={selDate}
              setSelDate={setSelDate}
              calcResult={calcResult}
              onClose={() => { setShowCalc(false); setSelDate(''); }}
              isMobile={true}
            />
          </div>
        </div>
      )}
    </div>
  );
}

// ── CALC PANEL COMPONENT ──
function CalcPanel({ calcPair, setCalcPair, selDate, setSelDate, calcResult, onClose, isMobile }) {
  return (
    <div>
      {isMobile && (
        <div style={{ width: '36px', height: '4px', background: '#E5E7EB', borderRadius: '2px', margin: '10px auto 0' }} />
      )}

      {/* Header */}
      <div style={{
        display: 'flex', justifyContent: 'space-between',
        alignItems: 'center', padding: '14px 20px',
        borderBottom: '1px solid #E5E7EB',
      }}>
        <div style={{ fontFamily: 'Arial', fontSize: '15px', fontWeight: 700, color: '#1B3A6B', letterSpacing: '0.06em' }}>
          CUSTOM FWD RATE
        </div>
        <button
          onClick={onClose}
          style={{
            width: '32px', height: '32px',
            background: '#F3F4F6', border: 'none',
            borderRadius: '50%', fontSize: '16px',
            cursor: 'pointer', color: '#374151',
            display: 'flex', alignItems: 'center',
            justifyContent: 'center',
          }}>
          ✕
        </button>
      </div>

      {/* Body */}
      <div style={{ padding: '16px 20px 28px' }}>

        {/* Currency pair */}
        <label style={{ display: 'block', fontFamily: 'Arial', fontSize: '11px', fontWeight: 600, color: '#6B7280', letterSpacing: '0.08em', marginBottom: '6px' }}>
          CURRENCY PAIR
        </label>
        <select
          value={calcPair}
          onChange={e => setCalcPair(e.target.value)}
          style={{
            display: 'block', width: '100%',
            background: '#F8FAFC',
            border: '1px solid #D1D5DB',
            borderRadius: '8px', padding: '10px 14px',
            fontSize: '15px', fontWeight: 600,
            color: '#111827', outline: 'none',
            marginBottom: '16px',
            cursor: 'pointer',
          }}>
          {['USDINR', 'EURINR', 'GBPINR', 'JPYINR', 'EURUSD'].map(p => (
            <option key={p} value={p}>{p}</option>
          ))}
        </select>

        {/* Date */}
        <label style={{ display: 'block', fontFamily: 'Arial', fontSize: '11px', fontWeight: 600, color: '#6B7280', letterSpacing: '0.08em', marginBottom: '6px' }}>
          SELECT DATE
        </label>
        <input
          type="date"
          value={selDate}
          onChange={e => setSelDate(e.target.value)}
          min={new Date().toISOString().split('T')[0]}
          style={{
            display: 'block', width: '100%',
            background: '#F8FAFC',
            border: '1px solid #D1D5DB',
            borderRadius: '8px', padding: '10px 14px',
            fontSize: '15px', color: '#111827',
            outline: 'none', colorScheme: 'light',
            boxSizing: 'border-box',
            marginBottom: '16px',
            cursor: 'pointer',
          }}
        />

        {/* Live note */}
        {selDate && (
          <div style={{
            background: '#F0FDF4', border: '1px solid #BBF7D0',
            borderRadius: '8px', padding: '8px 12px',
            fontFamily: 'Arial', fontSize: '11px',
            color: '#16A34A', fontWeight: 600,
            marginBottom: '16px',
          }}>
            ● Result updates live with spot rate
          </div>
        )}

        {/* Result */}
        {calcResult && (
          <div style={{ borderRadius: '10px', overflow: 'hidden', border: '1px solid #E5E7EB' }}>

            {/* FORWARD RATES section */}
            <div style={{ background: '#1B3A6B', padding: '8px 14px' }}>
              <div style={{ fontFamily: 'Arial', fontSize: '10px', fontWeight: 700, color: '#93C5FD', letterSpacing: '0.1em', textAlign: 'center', marginBottom: '10px' }}>
                FORWARD RATES
              </div>
              <div style={{ display: 'flex' }}>
                <div style={{ flex: 1, textAlign: 'center', borderRight: '1px solid rgba(255,255,255,0.2)', paddingRight: '10px' }}>
                  <div style={{ fontFamily: 'Arial', fontSize: '9px', fontWeight: 600, color: '#93C5FD', letterSpacing: '0.08em', marginBottom: '6px' }}>
                    FORWARD BID
                  </div>
                  <div style={{ fontFamily: 'monospace', fontSize: '24px', fontWeight: 700, color: '#4ADE80' }}>
                    {calcResult.fwdBid}
                  </div>
                </div>
                <div style={{ flex: 1, textAlign: 'center', paddingLeft: '10px' }}>
                  <div style={{ fontFamily: 'Arial', fontSize: '9px', fontWeight: 600, color: '#93C5FD', letterSpacing: '0.08em', marginBottom: '6px' }}>
                    FORWARD ASK
                  </div>
                  <div style={{ fontFamily: 'monospace', fontSize: '24px', fontWeight: 700, color: '#FCA5A5' }}>
                    {calcResult.fwdAsk}
                  </div>
                </div>
              </div>
            </div>

            {/* SPOT RATES section */}
            <div style={{ background: '#F0F4F8', padding: '10px 14px', borderBottom: '1px solid #E5E7EB' }}>
              <div style={{ fontFamily: 'Arial', fontSize: '10px', fontWeight: 700, color: '#1B3A6B', letterSpacing: '0.1em', marginBottom: '8px' }}>
                SPOT RATES
              </div>
              <div style={{ display: 'flex', gap: '8px' }}>
                <div style={{ flex: 1, background: '#fff', borderRadius: '8px', padding: '10px', textAlign: 'center', border: '1px solid #E5E7EB' }}>
                  <div style={{ fontFamily: 'Arial', fontSize: '9px', fontWeight: 600, color: '#6B7280', letterSpacing: '0.08em', marginBottom: '4px' }}>
                    SPOT BID
                  </div>
                  <div style={{ fontFamily: 'monospace', fontSize: '18px', fontWeight: 700, color: '#16A34A' }}>
                    {calcResult.spotBid}
                  </div>
                </div>
                <div style={{ flex: 1, background: '#fff', borderRadius: '8px', padding: '10px', textAlign: 'center', border: '1px solid #E5E7EB' }}>
                  <div style={{ fontFamily: 'Arial', fontSize: '9px', fontWeight: 600, color: '#6B7280', letterSpacing: '0.08em', marginBottom: '4px' }}>
                    SPOT ASK
                  </div>
                  <div style={{ fontFamily: 'monospace', fontSize: '18px', fontWeight: 700, color: '#DC2626' }}>
                    {calcResult.spotAsk}
                  </div>
                </div>
              </div>
            </div>

            {/* ANNUALISED FORWARD PREMIUM section */}
            <div style={{ background: '#F0F4F8', padding: '10px 14px', borderBottom: '1px solid #E5E7EB' }}>
              <div style={{ fontFamily: 'Arial', fontSize: '10px', fontWeight: 700, color: '#1B3A6B', letterSpacing: '0.1em', marginBottom: '8px' }}>
                ANNUALISED FORWARD PREMIUM
              </div>
              <div style={{ display: 'flex', gap: '8px' }}>
                <div style={{ flex: 1, background: '#fff', borderRadius: '8px', padding: '10px', textAlign: 'center', border: '1px solid #E5E7EB' }}>
                  <div style={{ fontFamily: 'Arial', fontSize: '9px', fontWeight: 600, color: '#6B7280', letterSpacing: '0.08em', marginBottom: '4px' }}>
                    BID
                  </div>
                  <div style={{ fontFamily: 'monospace', fontSize: '18px', fontWeight: 700, color: '#16A34A' }}>
                    {calcResult.bidPct} %
                  </div>
                </div>
                <div style={{ flex: 1, background: '#fff', borderRadius: '8px', padding: '10px', textAlign: 'center', border: '1px solid #E5E7EB' }}>
                  <div style={{ fontFamily: 'Arial', fontSize: '9px', fontWeight: 600, color: '#6B7280', letterSpacing: '0.08em', marginBottom: '4px' }}>
                    ASK
                  </div>
                  <div style={{ fontFamily: 'monospace', fontSize: '18px', fontWeight: 700, color: '#DC2626' }}>
                    {calcResult.askPct} %
                  </div>
                </div>
              </div>
            </div>

            {/* DAYS section */}
            <div style={{ background: '#F0F4F8', padding: '10px 14px' }}>
              <div style={{ fontFamily: 'Arial', fontSize: '10px', fontWeight: 700, color: '#1B3A6B', letterSpacing: '0.1em', marginBottom: '8px' }}>
                DAYS
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div style={{ display: 'flex', gap: '16px' }}>
                  <div>
                    <div style={{ fontFamily: 'Arial', fontSize: '9px', color: '#6B7280', marginBottom: '2px' }}>Pair</div>
                    <div style={{ fontFamily: 'Arial', fontSize: '13px', fontWeight: 700, color: '#1B3A6B' }}>{calcResult.pair}</div>
                  </div>
                  <div>
                    <div style={{ fontFamily: 'Arial', fontSize: '9px', color: '#6B7280', marginBottom: '2px' }}>Date</div>
                    <div style={{ fontFamily: 'Arial', fontSize: '13px', fontWeight: 700, color: '#374151' }}>{calcResult.date}</div>
                  </div>
                </div>
                <div style={{
                  background: '#1B3A6B', color: '#D4A843',
                  fontFamily: 'monospace', fontSize: '18px',
                  fontWeight: 700, padding: '8px 16px',
                  borderRadius: '8px', minWidth: '60px',
                  textAlign: 'center',
                }}>
                  {calcResult.days}
                </div>
              </div>
            </div>

          </div>
        )}
      </div>
    </div>
  );
}